import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

dayjs.extend(isSameOrAfter);

interface TodoData {
  id: number;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  status: number;
  status_text: string;
}

interface TodoFormValues
  extends Omit<TodoData, "id" | "status" | "status_text"> {
  id?: number;
}

interface AddStaffFormProps {
  onClose: () => void;
  onSubmit: () => void;
  isStaffModalOpen: boolean;
  editData?: TodoData | null;
}
// Date validation function
const isValidDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

const ToDoAdd: React.FC<AddStaffFormProps> = ({
  onClose,
  onSubmit,
  isStaffModalOpen,
  editData,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    start_date: "",
    end_date: "",
  });

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Task title is required")
      .max(255, "Task title must be less than 255 characters"),
    start_date: Yup.string()
      .required("Start date is required")
      .test("valid-date", "Invalid date format", (value) => {
        if (!value) return false;
        return dayjs(value).isValid();
      }),
    end_date: Yup.string()
      .nullable()
      .test("valid-date", "Invalid date format", (value) => {
        if (!value) return true; // Allow empty end date
        return dayjs(value).isValid();
      })
      .test(
        "same-or-after-start",
        "End Date cannot be earlier than Start Date.",
        function (value) {
          const { start_date } = this.parent;
          if (!value || !start_date) return true;
          return dayjs(value).isSameOrAfter(dayjs(start_date));
        },
      ),
    description: Yup.string().max(
      500,
      "Description must be less than 500 characters",
    ),
  });
  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      start_date: "",
      end_date: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        const { authenticatedCall } = actionsHandler(
          editData ? "todoById" : "todo",
        );

        const formattedStartDate = new Date(values.start_date)
          .toISOString()
          .split("T")[0];
        const formattedEndDate = values.end_date
          ? new Date(values.end_date).toISOString().split("T")[0]
          : null;

        const response = await dispatch(
          authenticatedCall({
            payload: {
              name: values.name,
              description: values.description,
              start_date: formattedStartDate,
              ...(formattedEndDate && { end_date: formattedEndDate }),
            },
            method: editData ? "put" : "post",
            customParams: editData ? [editData.id + "/"] : undefined,
          }),
        );

        if (response.ok) {
          toast.success(`Task ${editData ? "updated" : "added"} successfully`);
          formik.resetForm();
          onSubmit();
          onClose();
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Update form values when editData changes
  useEffect(() => {
    if (editData) {
      // Safely format dates to YYYY-MM-DD
      const formatDateWithTime = (dateString: string) => {
        if (!dateString) return "";
        try {
          // Convert DD-MM-YYYY to YYYY-MM-DD
          const [day, month, year] = dateString.split("-");
          return `${year}-${month}-${day}`;
        } catch (error) {
          console.error("Date formatting error:", error);
          return "";
        }
      };

      const formattedValues = {
        name: editData.name || "",
        description: editData.description || "",
        start_date: formatDateWithTime(editData.start_date),
        end_date: formatDateWithTime(editData.end_date),
      };

      formik.setValues(formattedValues);
    }
  }, [editData]);

  const handleDateChange = (name: string, value: Dayjs | null) => {
    const dateValue = value ? value.format("YYYY-MM-DD") : "";
    formik.setFieldValue(name, dateValue);
    formik.validateField("start_date");
    formik.validateField("end_date");
  };

  // Clear form and reset edit mode when closing
  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <div
      className={`tw-w-[350px] tw-fixed tw-bg-black/25 tw-top-0 tw-h-[100vh] tw-flex tw-items-center tw-justify-center tw-z-50 tw-transition-all tw-ease-in-out tw-duration-1000 ${
        isStaffModalOpen ? "tw-right-0" : "tw-right-[-265px]"
      }`}
    >
      <div className="tw-bg-white tw-shadow-lg tw-mx-auto tw-h-full tw-max-h-screen tw-w-[350px] tw-flex tw-flex-col">
        {/* Sticky Header */}
        <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b tw-sticky tw-top-0 tw-bg-white tw-z-10">
          <h2 className="tw-text-base tw-font-semibold">
            {editData ? "Edit Task" : "Add Task"}
          </h2>
          <button onClick={handleClose}>
            <img src={CloseIcon} alt="Close" className="tw-w-auto tw-h-auto" />
          </button>
        </div>

        <form
          onSubmit={formik.handleSubmit}
          className="tw-flex tw-flex-col tw-h-[calc(100vh-64px)]"
        >
          {/* Scrollable Content Area */}
          <div className="tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden">
            <div className="tw-px-4 tw-py-4 tw-space-y-6">
              {/* Task Title Input */}
              <div>
                <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                  Task Title <span className="tw-text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter task title"
                  className={`tw-w-full tw-h-11 tw-px-3 tw-border ${
                    formik.touched.name && formik.errors.name
                      ? "tw-border-red-500"
                      : "tw-border-[#D5D7DA]"
                  } tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500`}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                    {formik.errors.name}
                  </div>
                )}
              </div>

              {/* Start Date Input */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div>
                  <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                    Start Date <span className="tw-text-red-500">*</span>
                  </label>
                  <DatePicker
                    value={
                      formik.values.start_date
                        ? dayjs(formik.values.start_date)
                        : null
                    }
                    onChange={(value) => handleDateChange("start_date", value)}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        placeholder: "DD/MM/YYYY",
                        fullWidth: true,
                        error: !!formik.errors.start_date,
                        helperText: formik.errors.start_date,
                        required: true,
                        sx: {
                          "& .MuiInputBase-root": {
                            height: "44px",
                          },
                        },
                      },
                    }}
                  />
                </div>

                {/* End Date Input */}
                <div>
                  <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                    End Date
                  </label>
                  <DatePicker
                    value={
                      formik.values.end_date
                        ? dayjs(formik.values.end_date)
                        : null
                    }
                    format="DD/MM/YYYY"
                    onChange={(value) => handleDateChange("end_date", value)}
                    minDate={
                      formik.values.start_date
                        ? dayjs(formik.values.start_date)
                        : undefined
                    }
                    slotProps={{
                      textField: {
                        placeholder: "DD/MM/YYYY",
                        fullWidth: true,
                        error: !!formik.errors.end_date,
                        helperText: formik.errors.end_date,
                        sx: {
                          "& .MuiInputBase-root": {
                            height: "44px",
                          },
                        },
                      },
                    }}
                  />
                </div>
              </LocalizationProvider>

              {/* Contact Number Input */}
              <div>
                <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                  Description <span className="tw-text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  id="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={6}
                  placeholder="Write here..."
                  className={`tw-w-full tw-px-3 tw-border tw-py-3 ${
                    formik.touched.description && formik.errors.description
                      ? "tw-border-red-500"
                      : "tw-border-[#D5D7DA]"
                  } tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500`}
                ></textarea>
                {formik.touched.description && formik.errors.description && (
                  <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                    {formik.errors.description}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sticky Footer */}
          <div className="tw-px-4 tw-py-4 tw-border-t tw-mt-auto tw-bg-white">
            <button
              type="submit"
              disabled={
                isLoading ||
                !formik.values.name ||
                !formik.values.start_date ||
                !formik.values.description ||
                !!formik.errors.name ||
                !!formik.errors.start_date ||
                !!formik.errors.end_date
              }
              className="tw-w-full tw-h-11 tw-bg-[#146C94] tw-text-white tw-px-4 tw-rounded-md 
                       focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#146C94] 
                       focus:tw-ring-offset-2 tw-transition-colors disabled:tw-opacity-50"
            >
              {isLoading
                ? "Processing..."
                : editData
                  ? "Update Task"
                  : "Add Task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ToDoAdd;
